var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import React, { createContext, PureComponent } from 'react';
import PropTypes from 'prop-types';
import compute from 'compute-scroll-into-view';
import scrollIntoView from 'scroll-into-view-if-needed';
import throttle from 'lodash.throttle';
import Affix from '../Affix';
import Link from './link';
import cs from '../_util/classNames';
import { on, off } from '../_util/dom';
import { isFunction, isNumber, isString } from '../_util/is';
import { slide, findNode } from './utils';
import { ConfigConsumer } from '../ConfigProvider';
var Anchor = /** @class */ (function (_super) {
    __extends(Anchor, _super);
    function Anchor() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.links = {};
        _this.isScrolling = false;
        _this.state = {
            currentLink: '',
        };
        _this.addLink = function (hash, node) {
            if (!hash)
                return;
            _this.links[hash] = node;
        };
        _this.removeLink = function (hash) {
            delete _this.links[hash];
        };
        _this.getContainer = function () {
            var scrollContainer = _this.props.scrollContainer;
            if (isString(scrollContainer)) {
                return findNode(document, scrollContainer);
            }
            return scrollContainer || window;
        };
        _this.getContainerElement = function () {
            var scrollContainer = _this.props.scrollContainer;
            if (isString(scrollContainer)) {
                return findNode(document, scrollContainer);
            }
            if (scrollContainer && scrollContainer !== window) {
                return scrollContainer;
            }
            return document.documentElement || document.body;
        };
        _this.handleLinkClick = function (e, hash) {
            var currentLink = _this.state.currentLink;
            var _a = _this.props, onSelect = _a.onSelect, changeHash = _a.hash;
            if (!changeHash) {
                e.preventDefault();
            }
            _this.handleAnchorChange(hash);
            _this.scrollIntoView(hash);
            isFunction(onSelect) && onSelect(hash, currentLink);
        };
        _this.scrollIntoView = function (hash) {
            if (!hash)
                return;
            try {
                var node = findNode(document, hash);
                if (!node)
                    return;
                var boundary = _this.props.boundary;
                var block = boundary;
                var diff = 0;
                if (isNumber(boundary)) {
                    block = 'start';
                    diff = boundary;
                }
                var actions = compute(node, { block: block });
                if (!actions.length)
                    return;
                var _a = actions[0], el = _a.el, top_1 = _a.top;
                var targetTop = top_1 - diff;
                if (!_this.props.animation) {
                    return;
                }
                slide(el, targetTop, function () {
                    _this.isScrolling = false;
                });
                _this.isScrolling = true;
            }
            catch (e) {
                console.error(e);
            }
        };
        _this.handleScroll = function () {
            if (_this.isScrolling)
                return;
            var node = _this.getEleInViewport();
            if (node && node.id) {
                var hash = "#" + node.id;
                _this.handleAnchorChange(hash);
            }
        };
        _this.handleAnchorChange = function (hash) {
            if (!hash)
                return;
            if (!_this.links[hash] && _this.anchorRef) {
                var node = findNode(_this.anchorRef, "a[data-href='" + hash + "']");
                if (!node)
                    return;
                _this.links[hash] = node;
            }
            var currentLink = _this.state.currentLink;
            if (hash !== currentLink) {
                scrollIntoView(_this.links[hash], {
                    behavior: 'instant',
                    block: 'nearest',
                    scrollMode: 'if-needed',
                    boundary: _this.anchorRef,
                });
                _this.setState({
                    currentLink: hash,
                }, function () {
                    var onChange = _this.props.onChange;
                    isFunction(onChange) && onChange(hash, currentLink);
                });
            }
        };
        _this.getEleInViewport = function () {
            var result;
            var boundary = _this.props.boundary;
            var startTop = isNumber(boundary) ? boundary : 0;
            var dom = _this.getContainerElement();
            var rect = dom.getBoundingClientRect();
            var clientHeight = document.documentElement.clientHeight;
            Object.keys(_this.links).some(function (hash) {
                var node = findNode(document, hash);
                var inView = false;
                if (node) {
                    var top_2 = node.getBoundingClientRect().top;
                    if (_this.getContainer() === window) {
                        inView = top_2 >= startTop && top_2 <= clientHeight / 2;
                    }
                    else {
                        var offsetTop = top_2 - rect.top - startTop;
                        inView = offsetTop >= 0 && offsetTop < rect.height / 2;
                    }
                    if (inView) {
                        result = node;
                    }
                }
                return inView;
            });
            return result;
        };
        _this.renderAnchor = function (_a) {
            var getPrefixCls = _a.getPrefixCls;
            var _b = _this.props, style = _b.style, className = _b.className, children = _b.children, affix = _b.affix, affixStyle = _b.affixStyle, offsetTop = _b.offsetTop, offsetBottom = _b.offsetBottom;
            var currentLink = _this.state.currentLink;
            var prefixCls = getPrefixCls('anchor');
            var content = (React.createElement("div", { ref: function (ref) { return (_this.anchorRef = ref); }, className: cs(prefixCls, className), style: style },
                React.createElement("div", { className: prefixCls + "-line" }, currentLink && (React.createElement("div", { className: prefixCls + "-line-slider", ref: function (ref) { return (_this.lineRef = ref); } }))),
                React.createElement(AnchorContext.Provider, { value: {
                        currentLink: currentLink,
                        onLinkClick: _this.handleLinkClick,
                        addLink: _this.addLink,
                        removeLink: _this.removeLink,
                    } },
                    React.createElement("ul", { className: prefixCls + "-list" }, children))));
            return affix ? (React.createElement(Affix, { offsetBottom: offsetBottom, offsetTop: offsetTop, style: affixStyle }, content)) : (content);
        };
        return _this;
    }
    Anchor.prototype.componentDidMount = function () {
        this.scrollEventHandler = throttle(this.handleScroll, 30, { trailing: true });
        var hash = decodeURIComponent(location.hash);
        if (hash) {
            this.handleAnchorChange(hash);
            this.scrollIntoView(hash);
        }
        on(this.getContainer(), 'scroll', this.scrollEventHandler);
    };
    Anchor.prototype.componentDidUpdate = function () {
        var link = this.links[this.state.currentLink];
        if (link && this.lineRef) {
            this.lineRef.style.top = link.offsetTop + "px";
        }
    };
    Anchor.prototype.componentWillUnmount = function () {
        off(this.getContainer(), 'scroll', this.scrollEventHandler);
    };
    Anchor.prototype.render = function () {
        return React.createElement(ConfigConsumer, null, this.renderAnchor);
    };
    Anchor.displayName = 'Anchor';
    Anchor.Link = Link;
    Anchor.propTypes = {
        className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        style: PropTypes.object,
        scrollContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        affix: PropTypes.bool,
        offsetTop: PropTypes.number,
        offsetBottom: PropTypes.number,
        hash: PropTypes.bool,
        boundary: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.oneOf(['start', 'end', 'center', 'nearest']),
        ]),
    };
    Anchor.defaultProps = {
        // scrollContainer: window,
        animation: true,
        affix: true,
        hash: true,
        boundary: 'start',
    };
    return Anchor;
}(PureComponent));
export var AnchorContext = createContext({
    currentLink: '',
    onLinkClick: function (_e, _href) { },
    addLink: function (_href, _node) { },
    removeLink: function (_href) { },
});
export default Anchor;
